import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { SUPPORTED_LANGUAGES } from "@/i18n/constants";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy load page components for code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const GameReview = lazy(() => import("./pages/GameReview"));
const Glossary = lazy(() => import("./pages/Glossary"));
const GlossaryTerm = lazy(() => import("./pages/GlossaryTerm"));
const Articles = lazy(() => import("./pages/ArticlesDb"));
const ArticlePage = lazy(() => import("./pages/ArticlePageDb"));
const BonusPage = lazy(() => import("./pages/BonusPage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));

// Optimized QueryClient with caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Wrapper component to validate language parameter
const LanguageRoute = ({ children }: { children?: React.ReactNode }) => {
  const { lang } = useParams<{ lang: string }>();
  
  if (!lang || !SUPPORTED_LANGUAGES.includes(lang as typeof SUPPORTED_LANGUAGES[number])) {
    return <Navigate to="/en" replace />;
  }
  
  return (
    <LanguageProvider>
      <Suspense fallback={<PageLoader />}>
        {children || <Index />}
      </Suspense>
    </LanguageProvider>
  );
};

// Game review wrapper
const GameReviewWrapper = () => (
  <LanguageRoute>
    <GameReview />
  </LanguageRoute>
);

// NotFound wrapper with LanguageProvider
const NotFoundWrapper = () => (
  <LanguageProvider>
    <Suspense fallback={<PageLoader />}>
      <NotFound />
    </Suspense>
  </LanguageProvider>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ErrorBoundary>
          <Toaster />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* Root redirects to English */}
              <Route path="/" element={<Navigate to="/en" replace />} />
              
              {/* Language-prefixed routes with validation */}
              <Route path="/:lang" element={<LanguageRoute />} />
              
              {/* Game review pages */}
              <Route path="/:lang/games/:gameSlug" element={<GameReviewWrapper />} />
              
              {/* Comparison pages */}
              <Route path="/:lang/compare/:competitorSlug" element={<LanguageRoute><ComparisonPage /></LanguageRoute>} />
              
              {/* Glossary pages */}
              <Route path="/:lang/glossary" element={<LanguageRoute><Glossary /></LanguageRoute>} />
              <Route path="/:lang/glossary/:termSlug" element={<LanguageRoute><GlossaryTerm /></LanguageRoute>} />
              
              {/* Articles pages */}
              <Route path="/:lang/articles" element={<LanguageRoute><Articles /></LanguageRoute>} />
              <Route path="/:lang/articles/:slug" element={<LanguageRoute><ArticlePage /></LanguageRoute>} />
              
              {/* Bonus page */}
              <Route path="/:lang/bonus" element={<LanguageRoute><BonusPage /></LanguageRoute>} />
              
              {/* 404 for everything else */}
              <Route path="*" element={<NotFoundWrapper />} />
            </Routes>
          </BrowserRouter>
        </ErrorBoundary>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
